import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MessageCircle, Send, CheckCircle2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { API, WHATSAPP_COMMUNITY_URL } from "@/data/site";
import { SEO } from "@/components/Seo";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Message sent. We reply within two working days.");
    } catch {
      toast.error("Could not send right now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-paper-card border border-[#D6CEBF] rounded-xl px-4 py-3 text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-turmeric/70 text-sm";

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact | Pranivaa Kitchens"
        description="Talk to the kitchen — press, partnerships, catering pilots or questions about clean plant-forward eating. Pranivaa Kitchens replies within two working days."
        path="/contact"
      />
      <section className="pt-28 md:pt-40 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-clay font-semibold mb-5">
                Contact
              </p>
              <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-[1.08] text-ink font-semibold mb-6">
                Talk to the kitchen.
              </h1>
              <p className="text-base leading-relaxed text-ink-soft mb-10 max-w-md">
                Press, partnerships, catering pilots, or just a really good kanji story — we read
                everything and reply within two working days.
              </p>
              <div className="space-y-4">
                <a
                  href="mailto:inquiries@pranivaakitchens.com"
                  data-testid="contact-email-link"
                  className="flex items-center gap-3 text-sm text-ink-soft hover:text-beet transition-colors"
                >
                  <span className="w-10 h-10 rounded-full bg-turmeric-light text-turmeric-deep flex items-center justify-center">
                    <Mail size={16} />
                  </span>
                  inquiries@pranivaakitchens.com
                </a>
                <a
                  href={WHATSAPP_COMMUNITY_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-whatsapp-link"
                  className="flex items-center gap-3 text-sm text-ink-soft hover:text-beet transition-colors"
                >
                  <span className="w-10 h-10 rounded-full bg-leaf-light text-leaf flex items-center justify-center">
                    <MessageCircle size={16} />
                  </span>
                  WhatsApp the community team
                </a>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-7">
            {done ? (
              <div
                data-testid="contact-success-message"
                className="bg-leaf-light border border-leaf/20 rounded-3xl p-10 text-center h-full flex flex-col items-center justify-center"
              >
                <CheckCircle2 className="text-leaf mb-4" size={40} />
                <h2 className="font-serif text-2xl font-semibold text-ink mb-2">Message received.</h2>
                <p className="text-sm text-ink-soft max-w-sm">
                  Thanks, {form.name || "friend"}. We will write back to {form.email} soon.
                </p>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="bg-paper-card border border-[#E7E2D7] rounded-3xl p-7 md:p-10 shadow-sm space-y-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input
                    data-testid="contact-name-input"
                    className={inputCls}
                    placeholder="Your name *"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                  <input
                    data-testid="contact-email-input"
                    className={inputCls}
                    type="email"
                    placeholder="Email address *"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
                <textarea
                  data-testid="contact-message-input"
                  className={`${inputCls} min-h-40 resize-y`}
                  placeholder="What is on your mind? *"
                  required
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                />
                <button
                  data-testid="contact-form-submit-button"
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center gap-2 rounded-full bg-beet text-white font-bold text-sm px-8 py-4 transition-all duration-300 hover:bg-beet-deep hover:-translate-y-0.5 hover:shadow-xl disabled:opacity-60"
                >
                  <Send size={15} /> {loading ? "Sending..." : "Send Message"}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
